import { useCallback, useState } from "react"
import { useAppDispatch } from "../../../../app/hooks"
import { WorkflowDefinition, WorkflowObject } from "../api/workflowTypes"
import { AppTextFieldHandler } from "../../../../app/appTypes"
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material"
import { Localized } from "@fluent/react"
import { DocumentTypeSelect } from "../../../documents/controls/DocumentTypeSelect"
import { RequiredError } from "../../../../app/components/RequiredError"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { DocumentTypes } from "../../../documents/documentTypes"
import { WorkflowObjectSelect } from "../controls/WorkflowObjectSelect"
import { createWorkflowDefinition } from "../api/workflowApi"


export interface CreateWorkflowDefinitionDialogProps {
    onSucces: (workflowDefinition: WorkflowDefinition) => void
    onCancel: () => void
}

export const CreateWorkflowDefinitionDialog = (props: CreateWorkflowDefinitionDialogProps) => {
    const dispatch = useAppDispatch()

    const [definitionName, setDefinitionName] = useState<string>("")
    const [objectType, setObjectType] = useState<WorkflowObject>(WorkflowObject.DOCUMENT)
    const [objectSubType, setObjectSubType] = useState<string>("")

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")

    const handleDefinitionNameChange: AppTextFieldHandler = useCallback(e => {
        setDefinitionName(e.target.value)
    }, [])

    const handleDocumentTypeChange = useCallback((docType: DocumentTypes) => {
        if (docType) {
            setObjectSubType(docType)
        }
    }, []);

    const handleWorkflowObjectSelect = useCallback((objectType: WorkflowObject) => {
        if (objectType) {
            setObjectType(objectType)
        }
    }, []);


    const handleSaveClick = async () => {

        if (definitionName && objectType && objectSubType) {
            setSaving(true)
            try {
                const result =
                    await dispatch(createWorkflowDefinition({
                        name: definitionName,
                        objectType: objectType,
                        objectSubType: objectSubType
                    }))

                if (createWorkflowDefinition.fulfilled.match(result)) {
                    const definition = result.payload as WorkflowDefinition;
                    dispatch(showSuccess("saved"))
                    props.onSucces(definition)
                }
                else {
                    let errorMsg = "error"
                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    return (
        <Dialog open={true} fullWidth maxWidth='md'>
            <DialogTitle><Localized id='workflow-create-definition'>_New WFL definition</Localized></DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ padding: 2 }}>
                    <Grid item xs={4}>
                        <WorkflowObjectSelect required={true} onChange={handleWorkflowObjectSelect} />
                    </Grid>
                    <Grid item xs={4}>
                        <DocumentTypeSelect required={true} onChange={handleDocumentTypeChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            helperText={definitionName === '' && <RequiredError />}
                            required
                            fullWidth
                            InputLabelProps={{
                                shrink: true
                            }}
                            value={definitionName}
                            error={definitionName === ''}
                            inputProps={{ maxLength: 50 }}
                            onChange={handleDefinitionNameChange}
                            label={<Localized id=""><span>Name</span></Localized>}
                        />
                    </Grid>

                </Grid>
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSaveClick}></SaveButton>
                <CancelButton loading={saving} onClick={props.onCancel}></CancelButton>
            </DialogActions>
        </Dialog>
    );
}
